/**
 * MSA-443: Fast Simon autocomplete panel restyle — shadow-root CSS injection.
 *
 * The FS autocomplete bundle (fast-simon-autocomplete-init.umd.js, loaded
 * site-wide under the fs_autocomplete gate) renders the search panel inside
 * OPEN shadow roots on div.fast-ac-container host elements. Page-level CSS
 * cannot reach that content, so this engine copies the CSS text held in
 * snippets/FSAutocompleteStyle.liquid's <template id="fs-autocomplete-css-source">
 * into FS's own style#autocomplete-custom-css slot inside every shadow root.
 *
 * SECOND JOB (added for the page-dimming overlay, Figma 5294:11788): toggling
 * the light-DOM `.pd-fs-autocomplete-overlay` element (declared alongside the
 * <template> in FSAutocompleteStyle.liquid) in and out of view as panels
 * open/close — see observeOpenState() below. This is a real page element, not
 * shadow content, so it needs no CSS injection; it just needs the same
 * per-host shadow-root plumbing this file already has.
 *
 * WHY APPEND INTO FS's SLOT (not a separate <style>): each shadow root's
 * children are, in order, link#fs-autocomplete-styles (FS's stylesheet) →
 * div#fast-autocomplete-… (the panel) → style#autocomplete-custom-css (FS's
 * "no-code custom CSS" slot, LAST child). That slot holds a merchant-authored,
 * !important-HEAVY stylesheet (measured live: ~11kB / 130 !important decls that
 * set the exact heading/title/price properties this restyle overrides).
 * Appending our text to the END of that slot is the only position that (a) sits
 * after ALL of FS's CSS and (b) wins same-specificity !important ties by source
 * order. Our companion rules in FSAutocompleteStyle.liquid carry !important on
 * every FS-contested property for exactly this reason.
 *
 * THE ORDERING RACE (fixed here vs the original #226 engine): FS attaches the
 * shadow root with only link#fs-autocomplete-styles, then builds the panel and
 * the #autocomplete-custom-css slot a beat later. The #226 engine, finding no
 * slot on its first sweep, appended a fallback <style> immediately — which FS
 * then inserted its slot AFTER, so our CSS lost same-specificity ties to FS's
 * later !important slot rules. This engine instead WAITS (bounded retry) for the
 * slot and appends into it; the own-<style> fallback only fires once a host's
 * retry budget is spent (FS bundle changed / slot never appears).
 *
 * PER-HOST retry budget (not a shared counter): two hosts coexist (.autocomplete
 * .wide + .list) and FS can destroy/recreate hosts mid-session (e.g. a
 * breakpoint-crossing resize). A single shared counter would let a second host,
 * appearing while an earlier stuck host has run the counter near its cap, skip
 * the slot-wait and fall back BEFORE its own slot exists — reintroducing the very
 * race this engine fixes. Each host therefore carries its own monotonic
 * data-pd-autocomplete-tries budget. Monotonic-per-host also removes the #226
 * "counter reset on every document mutation unbounds the retry chain" hazard:
 * mutations only ever advance a host toward its fallback, never reset it, so the
 * retry chain always terminates (every pending host reaches its budget → styled).
 *
 * Deliberate divergences from assets/fast-simon.js's SERP hook pattern:
 * - No window.SerpOptions / fast-serp-ready dual-init: the autocomplete bundle
 *   exposes no hook API or ready event (confirmed in the MSA-443 capture).
 * - Per-host idempotency (data-pd-autocomplete-styled on each host) instead of
 *   one global boolean: FS can destroy and recreate hosts; a fresh host must
 *   be re-processed while a stable one isn't rewritten.
 * - The document MutationObserver runs for the full page lifetime. Do NOT add
 *   an observer.disconnect() "cleanup" — hosts can (re)appear indefinitely,
 *   and a disconnected observer silently un-styles recreated panels.
 */
(() => {
  const CSS_SOURCE_ID = "fs-autocomplete-css-source";
  const CSS_MARK = "/* pd-msa-443 */";
  const HOST_SELECTOR = ".fast-ac-container";
  const OVERLAY_SELECTOR = "[data-pd-fs-autocomplete-overlay]";
  const MAX_RETRIES = 40; // ~40 × 250ms ≈ 10s per host before the fallback fires
  // .mobile-fast-ac-container is a distinct class that HOST_SELECTOR does not
  // match — its shadow root never receives a populated panel (capture), so it
  // is deliberately not processed.

  if (window.__pdFsAutocompleteInit) return;
  window.__pdFsAutocompleteInit = true;

  // Page-dimming overlay (Figma 5294:11788) behind the OPEN panel — see
  // FSAutocompleteStyle.liquid for the element + its CSS. FS exposes no
  // ready/open event for the autocomplete bundle, so open/closed state is
  // inferred from whether a host's shadow root currently has a `.autocomplete`
  // element: VALIDATE (live shadow DOM, 2026-07-15) confirmed FS destroys that
  // element entirely on close (query cleared) and builds a fresh one on open,
  // rather than toggling its visibility in place.
  const openHosts = new Set();
  function updateOverlay() {
    const overlay = document.querySelector(OVERLAY_SELECTOR);
    if (overlay) overlay.classList.toggle("is-open", openHosts.size > 0);
  }
  function observeOpenState(host) {
    // Independent of the CSS-injection retry state below — open/close
    // tracking only needs the shadow root to exist, not the custom-CSS slot.
    if (host.dataset.pdAutocompleteOverlayObserved === "1") return;
    const root = host.shadowRoot;
    if (!root) return;
    host.dataset.pdAutocompleteOverlayObserved = "1";
    const check = () => {
      if (root.querySelector(".autocomplete")) openHosts.add(host);
      else openHosts.delete(host);
      updateOverlay();
    };
    new MutationObserver(check).observe(root, { childList: true, subtree: true });
    check();
  }

  let cssText = null;
  function getCssText() {
    if (cssText === null) {
      const tpl = document.getElementById(CSS_SOURCE_ID);
      cssText = tpl ? `\n${CSS_MARK}\n${tpl.content.textContent}` : "";
    }
    return cssText;
  }

  function appendFallbackStyle(root, css) {
    // Last-resort own-<style> as the shadow root's LAST child, only after a
    // host's slot-wait budget is spent. Idempotent via the data attribute.
    if (!root.querySelector("style[data-pd-autocomplete-css]")) {
      const style = document.createElement("style");
      style.setAttribute("data-pd-autocomplete-css", "");
      style.textContent = css;
      root.appendChild(style);
    }
  }

  function injectInto(host) {
    if (host.dataset.pdAutocompleteStyled === "1") return true;
    const css = getCssText();
    if (!css) {
      host.dataset.pdAutocompleteStyled = "1"; // processed; nothing to inject
      return true;
    }

    const root = host.shadowRoot;
    const slot = root && root.querySelector("#autocomplete-custom-css");
    if (slot) {
      // Append into FS's own custom-CSS slot (the shadow root's LAST child):
      // our rules land after all of FS's !important CSS, so equal-specificity
      // ties resolve in our favour by source order. Append, never overwrite —
      // a merchant-authored FS-dashboard custom-CSS value must be preserved.
      // CSS_MARK keeps the append idempotent.
      if (!slot.textContent.includes(CSS_MARK)) slot.textContent += css;
      host.dataset.pdAutocompleteStyled = "1";
      return true;
    }

    // Still waiting: FS hasn't attached the shadow root yet, OR has attached it
    // but hasn't built its #autocomplete-custom-css slot yet. Do NOT append a
    // premature <style> — it would land before the slot FS is about to insert
    // and lose same-specificity ties (the original #226 fallback-before-slot
    // race). Both waits count toward the SAME per-host budget (data attribute,
    // not a shared counter → no cross-host race; monotonic → page mutations
    // can't unbound it). This also bounds the retry chain even if a host's
    // shadow root never attaches at all — the chain always terminates because
    // every pending host reaches MAX_RETRIES.
    const tries = (parseInt(host.dataset.pdAutocompleteTries, 10) || 0) + 1;
    host.dataset.pdAutocompleteTries = String(tries);
    if (tries >= MAX_RETRIES) {
      // Budget spent (FS bundle changed / slot never appeared). If a shadow
      // root exists, append the last-resort fallback so the panel still gets
      // our CSS; if not even a root attached, just give up so the chain ends.
      // A host FS later destroys and recreates re-enters with a fresh budget.
      if (root) appendFallbackStyle(root, css);
      host.dataset.pdAutocompleteStyled = "1";
      return true;
    }
    return false; // shadow root / slot not ready — retry
  }

  function sweep() {
    let allDone = true;
    for (const host of document.querySelectorAll(HOST_SELECTOR)) {
      observeOpenState(host);
      if (!injectInto(host)) allDone = false;
    }
    return allDone;
  }

  // The retry chain terminates without a global counter: each unstyled host's
  // data-pd-autocomplete-tries advances by one per sweep and, if its slot never
  // appears, reaches MAX_RETRIES → fallback → styled. So every pending host
  // resolves within MAX_RETRIES sweeps and sweep() eventually returns true,
  // stopping the chain. The document observer restarts it only when a genuinely
  // new host appears. (Frequent unrelated mutations just advance pending hosts
  // toward their fallback faster — never reset them, never unbound the chain.)
  let retryTimer = null;
  function scheduleRetry() {
    if (retryTimer !== null) return;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (!sweep()) scheduleRetry();
    }, 250);
  }

  new MutationObserver(() => {
    if (!sweep()) scheduleRetry();
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (!sweep()) scheduleRetry();
})();